import { ChevronLeft } from "lucide-react";
import { useState } from "react";
import { post, type Me, type Trip } from "../lib/api.ts";
import { CATEGORIES_TAB, googlePlaceId, Link, tripPath, type TripSection } from "../lib/router.tsx";
import { isAdmin } from "../lib/rights.ts";
import { ICON, reason } from "../ui/index.ts";
import { ActivityPage } from "./ActivityPage.tsx";
import { EditorMapPage } from "./EditorMapPage.tsx";
import { editorSurface } from "./EditorSurfaces.tsx";
import { EditorBoard } from "./deliverables/EditorBoard.tsx";
import { Inbox } from "./Inbox.tsx";
import { MediaLibrary } from "./media/MediaLibrary.tsx";
import { MapPage } from "./MapPage.tsx";
import { OverviewPage } from "./OverviewPage.tsx";
import { PeoplePage } from "./PeoplePage.tsx";
import { PlacesPage } from "./PlacesPage.tsx";
import { ProductionTab } from "./ProductionTab.tsx";
import { RouteSharing } from "./RouteSharing.tsx";
import { SettingsPage, YourSharing } from "./SettingsPage.tsx";
import { SourcesPage } from "./SourcesPage.tsx";
import { StoragePage } from "./StoragePage.tsx";
import { SyncPage } from "./SyncPage.tsx";

/** Section titles; editors see the library as the incoming footage they work from. */
export function sectionTitle(section: TripSection): string {
  return SECTION_TITLE[section];
}
export const SECTION_TITLE: Record<TripSection, string> = {
  overview: "Overview",
  sync: "Media Sync",
  sources: "Sync sources",
  places: "Places and categories",
  map: "Map & Timeline",
  media: "Media Library",
  tags: "Tags",
  deliverables: "Deliverables",
  people: "People & Sync",
  storage: "Storage",
  activity: "Activity",
  settings: "Settings",
};

/** One trip section. The trip itself is loaded by the shell and passed in. */
export function TripPage({
  trip,
  section,
  itemId,
  me,
  refresh,
}: {
  trip: Trip;
  section: TripSection;
  /** What is being looked at inside the section — today, one tag. */
  itemId: string | null;
  me: Me;
  refresh: () => Promise<void>;
}) {
  const [error, setError] = useState<string | null>(null);
  const admin = isAdmin(trip.roles);
  // An editor's own surfaces: the map is theirs and is built (EditorMapPage); deliverables are
  // still a placeholder. See EditorSurfaces.
  const soon = editorSurface(section, trip.roles);
  // The library already says what it is twice over — in the bar at the foot and in its own
  // Media/Tags strip — so a page head there is a third copy, and it pushes the first row of
  // footage off the screen. The heading itself stays for a screen reader, which has no strip
  // to read.
  // The editor's map is a workspace rather than a document: its panels are sized to the window,
  // and a heading that repeats the section already named in the sidebar costs them a quarter of
  // their height. Same bargain as the library's, for the same reason.
  const bareHead = section === "media" || section === "tags" || soon === "map";

  async function lifecycle(action: "START" | "END") {
    setError(null);
    try {
      await post(`/v1/trips/${trip.id}/lifecycle`, {
        action,
        baseWindowVersion: trip.windowVersion,
      });
      await refresh();
    } catch (e) {
      setError(reason(e as Error));
    }
  }

  return (
    <section className="stack">
      {bareHead ? (
        <h1 className="sr">{sectionTitle(section)}</h1>
      ) : (
        <div className="page-head">
          <div className="grow">
            {section === "sources" && (
              <Link to={tripPath(trip.id, "sync")} className="back-link">
                <ChevronLeft {...ICON} aria-hidden="true" />
                Sync
              </Link>
            )}
            {section === "places" && (
              <Link to={tripPath(trip.id, "settings")} className="back-link">
                <ChevronLeft {...ICON} aria-hidden="true" />
                Settings
              </Link>
            )}
            <h1>{sectionTitle(section)}</h1>
            <p className="muted small" style={{ margin: 0 }}>
              {trip.name} ·{" "}
              {section === "sync"
                ? "Cloud Library"
                : section === "sources"
                  ? "Source permissions"
                  : section === "places"
                    ? "The trip's places"
                    : trip.state === "ACTIVE" && trip.captureStartAt
                      ? `Capturing since ${new Date(trip.captureStartAt).toLocaleString()}`
                      : trip.state === "ENDED" && trip.captureEndAt
                        ? `Capture ended ${new Date(trip.captureEndAt).toLocaleString()}`
                        : "Capture has not started"}
            </p>
          </div>
          {admin && section === "overview" && trip.state === "DRAFT" && (
            <button className="button primary" onClick={() => lifecycle("START")}>
              Start trip
            </button>
          )}
          {admin && section === "overview" && trip.state === "ACTIVE" && (
            <button className="button" onClick={() => lifecycle("END")}>
              End capture
            </button>
          )}
        </div>
      )}
      {error && (
        <p role="alert" className="error">
          {error}
        </p>
      )}

      <RouteSharing trip={trip} />
      {(section === "overview" || section === "media") && <Inbox tripId={trip.id} />}
      {section === "overview" && <OverviewPage trip={trip} me={me} />}
      {/* One library, two tabs: Media is where you land, Tags is a swipe away. */}
      {(section === "media" || section === "tags") && (
        <MediaLibrary trip={trip} me={me} tab={section} itemId={itemId} />
      )}
      {section === "deliverables" &&
        (soon === "deliverables" ? (
          <EditorBoard trip={trip} me={me} />
        ) : (
          <ProductionTab trip={trip} me={me} />
        ))}
      {section === "people" && <PeoplePage trip={trip} me={me} />}
      {section === "storage" && <StoragePage trip={trip} />}
      {section === "activity" && <ActivityPage trip={trip} />}
      {section === "map" &&
        (soon === "map" ? <EditorMapPage trip={trip} me={me} /> : <MapPage trip={trip} />)}
      {section === "sync" && <SyncPage trip={trip} />}
      {section === "sources" && <SourcesPage trip={trip} />}
      {section === "places" && (
        <PlacesPage
          trip={trip}
          tab={itemId === CATEGORIES_TAB ? "categories" : "places"}
          adding={googlePlaceId(itemId)}
        />
      )}
      {section === "settings" && <SettingsPage trip={trip} me={me} lifecycle={lifecycle} />}
      {section === "overview" && <YourSharing trip={trip} />}
    </section>
  );
}
